'use client'

import { useState, useCallback, useEffect } from 'react'

interface CookieOptions {
  days?: number
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
}

class CookieManager {
  /**
   * Set a cookie with the given name and value
   */
  static setCookie(name: string, value: string, options: CookieOptions = {}): void {
    if (typeof window === 'undefined') return

    const { days = 7, path = '/', domain, secure = false, sameSite = 'lax' } = options

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`

    if (days) {
      const date = new Date()

      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
      cookieString += `; expires=${date.toUTCString()}`
    }

    if (path) cookieString += `; path=${path}`
    if (domain) cookieString += `; domain=${domain}`
    if (secure) cookieString += `; secure`
    if (sameSite) cookieString += `; samesite=${sameSite}`

    document.cookie = cookieString
  }

  /**
   * Get a cookie value by name
   */
  static getCookie(name: string): string | null {
    if (typeof window === 'undefined') return null

    const nameEQ = `${encodeURIComponent(name)}=`
    const cookies = document.cookie.split(';')

    for (const cookie of cookies) {
      const c = cookie.trim()

      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length))
      }
    }

    return null
  }

  /**
   * Remove a cookie by setting its expiration date to the past
   */
  static removeCookie(name: string, options: Omit<CookieOptions, 'days'> = {}): void {
    if (typeof window === 'undefined') return

    const { path = '/', domain } = options

    let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`

    if (path) cookieString += `; path=${path}`
    if (domain) cookieString += `; domain=${domain}`

    document.cookie = cookieString
  }

  /**
   * Check if a cookie exists
   */
  static hasCookie(name: string): boolean {
    return this.getCookie(name) !== null
  }

  /**
   * Get all cookies as an object
   */
  static getAllCookies(): Record<string, string> {
    if (typeof window === 'undefined') return {}

    const cookies: Record<string, string> = {}
    const cookieArray = document.cookie.split(';')

    for (const cookie of cookieArray) {
      const [name, value] = cookie.trim().split('=')

      if (name && value) {
        cookies[decodeURIComponent(name)] = decodeURIComponent(value)
      }
    }

    return cookies
  }
}

/**
 * React hook for managing cookies with reactive state
 */
export function useCookie(
  name: string,
  defaultValue: string = ''
): [string | null, (value: string, options?: CookieOptions) => void, () => void, boolean] {
  const [cookieValue, setCookieValue] = useState<string | null>(null)
  const [, setIsLoading] = useState(true)

  // Initialize cookie value on mount
  useEffect(() => {
    const value = CookieManager.getCookie(name)

    setCookieValue(value || defaultValue || null)
    setIsLoading(false)
  }, [name, defaultValue])

  // Set cookie function
  const setCookie = useCallback(
    (value: string, options?: CookieOptions) => {
      CookieManager.setCookie(name, value, options)
      setCookieValue(value)
    },
    [name]
  )

  // Remove cookie function
  const removeCookie = useCallback(() => {
    CookieManager.removeCookie(name)
    setCookieValue(null)
  }, [name])

  // Check if cookie exists
  const hasCookie = cookieValue !== null

  return [cookieValue, setCookie, removeCookie, hasCookie]
}

/**
 * Hook specifically for authentication token management
 */
export function useAuthToken(): [string | null, (token: string) => void, () => void, boolean] {
  const [token, setToken, removeToken, hasToken] = useCookie('auth_token', '')

  const setAuthToken = useCallback(
    (token: string) => {
      setToken(token, {
        days: 30, // Token expires in 30 days
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax',
        path: '/'
      })
    },
    [setToken]
  )

  const clearAuthToken = useCallback(() => {
    removeToken()
  }, [removeToken])

  return [token, setAuthToken, clearAuthToken, hasToken]
}

export default CookieManager
